import * as Sentry from "@sentry/react";
import { ButtonBase, Tooltip } from "@mui/material";
import { classes } from "portal/theme/theme";
import { IconImage } from "./IconImage";
import { isNil } from "common/utils/identity";
import { useDistinctClickTypes } from "portal/utils/hooks/useDistinctClickTypes";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import React, { FC, FunctionComponent, useCallback, useState } from "react";

/**
 * General purpose component for rendering an image.
 * Supports:
 * - making the button clickable, double clickable (and long press-able for mobile)
 * - gracefully handling loading and error states visually
 * - callback for when the image is done loading
 * - supporting tooltips
 * - supporting overlays
 * - supporting error specific overlays
 */
export interface ImageProperties {
  id?: string;
  alt?: string;
  src?: string;
  isSrcLoading?: boolean;
  isSrcError?: boolean;
  tooltip?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onRightClick?: (e: React.MouseEvent) => void;
  onSrcError?: () => ImageSrcErrorResult;
  renderOverlay?: () => React.ReactNode;
  className?: string;
}

export interface ImageSrcErrorResult {
  recover: boolean; // true = caller is retrying (keep skeleton; don't show broken image)
}

interface ImageProps {
  image?: ImageProperties;
  onImageLoaded?: (id?: string) => void;
  renderErrorOverlay?: () => React.ReactNode;
  className?: {
    imageWrapper?: string; // high level div
    imageContainer?: string; // lower level div closer to the image
    image?: string;
  };
}

enum LoadingState {
  NOT_LOADED,
  SUCCESS,
  ERROR,
}
export const Image: FunctionComponent<ImageProps> = ({
  image,
  onImageLoaded,
  renderErrorOverlay,
  className,
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.NOT_LOADED
  );

  // reset loading state when source on an image changes
  const [prevSrc, setPrevSrc] = useState<string | undefined>();
  if (image?.src !== prevSrc) {
    setLoadingState(LoadingState.NOT_LOADED);
    setPrevSrc(image?.src);
  }

  const onLoad = useCallback(
    (success: boolean, id?: string) => {
      setLoadingState(success ? LoadingState.SUCCESS : LoadingState.ERROR);
      onImageLoaded?.(id);
    },
    [onImageLoaded]
  );

  const { events: distinctClickEvents, suppressContextClassNames } =
    useDistinctClickTypes(
      {
        onClick: image?.onClick,
        onDoubleClick: image?.onDoubleClick,
        onLongPress: image?.onDoubleClick,
        onRightClick: image?.onRightClick,
      },
      {}
    );

  const suppressContextMenu = (e: React.MouseEvent): void => {
    e.preventDefault();
  };

  if (!image) {
    return (
      <div
        className="relative w-full h-full"
        onContextMenu={suppressContextMenu}
      >
        <IconImage
          icon={(className) => <ImageNotSupportedIcon className={className} />}
        />
        {renderErrorOverlay?.()}
      </div>
    );
  }

  if (loadingState === LoadingState.ERROR || image.isSrcError) {
    if (image.isSrcError) {
      Sentry.logger.error("image.isSrcError", {
        source: "image.isSrcError",
        id: image.id,
        alt: image.alt,
        src: image.src,
      });
    }
    return (
      <div
        className="relative w-full h-full"
        onContextMenu={suppressContextMenu}
      >
        <IconImage
          icon={(className) => <BrokenImageIcon className={className} />}
        />
        {renderErrorOverlay?.()}
      </div>
    );
  }
  const {
    tooltip,
    renderOverlay,
    onClick,
    onDoubleClick,
    onRightClick,
    src,
    alt,
    id,
    onSrcError,
  } = image;
  const imageProps = {
    className: classes(className?.image, image.className),
    src,
    alt,
    onLoad: () => {
      onLoad(true, id);
    },
    onError: () => {
      if (onSrcError?.().recover) {
        setLoadingState(LoadingState.NOT_LOADED);
        return;
      }
      Sentry.logger.error("image element failed to load", {
        source: "img.onError",
        id,
        alt,
        src,
      });
      onLoad(false, id);
    },
  };
  return (
    <div className={classes("relative w-full h-full", className?.imageWrapper)}>
      <Tooltip
        title={
          tooltip ? (
            <div className="whitespace-pre-line">{tooltip}</div>
          ) : undefined
        }
        placement="bottom"
      >
        <div
          className={classes(
            "aspect-square max-h-full max-w-full relative",
            className?.imageContainer
          )}
        >
          {!isNil(onClick) || !isNil(onDoubleClick) || !isNil(onRightClick) ? (
            <ButtonBase
              {...distinctClickEvents}
              className={classes(
                "w-full h-full p-0",
                suppressContextClassNames
              )}
            >
              <ImageElement {...imageProps} />
            </ButtonBase>
          ) : (
            <ImageElement {...imageProps} />
          )}
          {renderOverlay?.()}
        </div>
      </Tooltip>

      {loadingState === LoadingState.NOT_LOADED || image.isSrcLoading ? (
        <div className="absolute inset-0 top-0 right-0 object-cover bg-gray-600 animate-pulse"></div>
      ) : undefined}
    </div>
  );
};

interface ImageElementProps {
  className?: string;
  src?: string;
  alt?: string;
  onLoad?: () => void;
  onError?: () => void;
}
const ImageElement: FC<ImageElementProps> = ({
  className,
  src,
  alt,
  onLoad,
  onError,
}) => {
  if (!src) {
    return;
  }
  return (
    <img
      className={classes("object-contain w-full h-full", className)}
      src={src}
      alt={alt}
      loading="eager"
      onLoad={onLoad}
      onError={onError}
      draggable="false"
    />
  );
};
